import { Address } from "@app/address/model/address";
import { ActionPayloadGeneric } from "@app/root-store/actions";
import { createCrudActionSets, createNoPropsAction } from "@app/root-store/actions/action-creators";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";

/**
 * A payload used when performing an operation on a single entity
 */
export type ActionPayload = ActionPayloadGeneric & {
    address: Address;
    addressType?: string;
    entityId?: number;
};

/**
 * A payload used when loading an entity
 */
interface LoadActionPayload {
    addressId: number;
}

/**
 * An action result that returns a collection of entities
 */
export interface BulkActionResult {
    addresses: ClareDbCollection<Address>;
}

/**
 * An action result that returns a single entity
 */
export interface ActionResult {
    address: Address;
}

/**
 * @description Used in the action creators to generate an action type like:
 * "[Entity Management] Load Entity"
 */
const actionCategory = "Address";

/**
 * Create the CRUD set of actions associated with the action category.
 * Must send in appropriate payload types for a single and load and result types for single and bulk.
 * A request, success and failure action are created for each CRUD operation.
 * Each action set should be deconstructed into consts from the result.
 */
export const {
    loadSingle: loadSingleSet,
    create: createSet,
    update: updateSet,
    delete: deleteSet,
} = createCrudActionSets<ActionPayload, LoadActionPayload, ActionResult, BulkActionResult>(actionCategory);

/**
 * Create each action from the deconstructed action sets created by createCrudActionSets.
 * Each action is strongly typed and should be used in accordance with the NgRx docs.
 * Any action is optional and can be omitted by simply not desctructuring it.
 */
export const { request: get, success: getSuccess, failure: getFailure } = loadSingleSet;
export const { request: create, success: createSuccess, failure: createFailure } = createSet;
export const { request: update, success: updateSuccess, failure: updateFailure } = updateSet;
export const { request: deleteItem, success: deleteSuccess, failure: deleteFailure } = deleteSet;

/**
 * An action to clear the data in the store
 */
export const clear = createNoPropsAction(actionCategory, "Clear State");
